import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';

export interface ChangelogItem {
	/** Área em negrito no início do item ("Hole Details · Timeline") */
	area?: string;
	text: string;
}

export interface ChangelogSection {
	/** Adicionado | Alterado | Corrigido | Removido */
	type: string;
	items: ChangelogItem[];
}

export interface ChangelogVersion {
	version: string;
	/** AAAA-MM-DD */
	date: string;
	sections: ChangelogSection[];
}

/**
 * Lê assets/atualizacoes/CHANGELOG.md (formato Keep a Changelog) — a fonte única das
 * atualizações do sistema: a tela de Atualizações, o sino de notificações e a tela App Changes.
 */
@Injectable({ providedIn: 'root' })
export class ChangelogService {
	readonly url = 'assets/atualizacoes/CHANGELOG.md';

	private _versions$: Observable<ChangelogVersion[]>;

	constructor(private _http: HttpClient) { }

	/** Markdown cru (para renderizar na tela de Atualizações). */
	getMarkdown(): Observable<string> {
		// ?v= evita cache do navegador depois de um deploy
		return this._http.get(`${this.url}?v=${Date.now()}`, { responseType: 'text' });
	}

	/** Versões já separadas, da mais nova para a mais antiga. */
	getVersions(): Observable<ChangelogVersion[]> {
		if (!this._versions$) {
			this._versions$ = this.getMarkdown().pipe(
				map((md) => parseChangelog(md)),
				catchError(() => of([])),
				shareReplay(1)
			);
		}
		return this._versions$;
	}
}

export function parseChangelog(md: string): ChangelogVersion[] {
	const versions: ChangelogVersion[] = [];
	let version: ChangelogVersion | null = null;
	let section: ChangelogSection | null = null;

	const lines = md.replace(/<!--[\s\S]*?-->/g, '').split(/\r?\n/);
	for (const raw of lines) {
		const line = raw.trim();

		const v = line.match(/^##\s+\[?([^\]\s]+)\]?\s*(?:-\s*(\S+))?/);
		if (v && !line.startsWith('###')) {
			version = { version: v[1], date: v[2] ?? '', sections: [] };
			versions.push(version);
			section = null;
			continue;
		}

		const s = line.match(/^###\s+(.+)/);
		if (s && version) {
			section = { type: s[1].trim(), items: [] };
			version.sections.push(section);
			continue;
		}

		const i = line.match(/^[-*]\s+(.+)/);
		if (i && section) {
			const area = i[1].match(/^\*\*(.+?):?\*\*:?\s*(.*)$/);
			section.items.push(area ? { area: area[1].replace(/:$/, ''), text: area[2] } : { text: i[1] });
		}
	}
	return versions;
}
